import React, { useState, useEffect } from 'react';
import {
    Card, Form, Input, InputNumber, Select, DatePicker, Button,
    Table, Typography, Space, message, Row, Col, Tag, Alert,
    Divider, Statistic, Spin, Grid
} from 'antd';
import {
    ArrowLeftOutlined, PlusOutlined, DeleteOutlined,
    CalculatorOutlined, SaveOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const fmt = (v) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Formula: dried_weight = wet × (100 - initial_mc) / (100 - target_mc)
const calcExpected = (wet, initialMc, targetMc) => {
    if (!wet || initialMc == null || targetMc == null || initialMc >= 100 || targetMc >= 100) return null;
    // Drying cannot add weight; a target above the initial MC means nothing to remove.
    if (targetMc >= initialMc) return Math.round(wet * 10000) / 10000;
    return Math.round(wet * (100 - initialMc) / (100 - targetMc) * 10000) / 10000;
};

const DryingForm = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [branches, setBranches] = useState([]);
    const [availableLots, setAvailableLots] = useState([]);
    const [selectedLots, setSelectedLots] = useState([]);
    const [lotsLoading, setLotsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [targetMc, setTargetMc] = useState(null);
    const [branchId, setBranchId] = useState(null);

    useEffect(() => {
        client.get('/master/branches').then(({ data }) => setBranches(data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (branchId) fetchAvailableLots(branchId);
        else setAvailableLots([]);
    }, [branchId]);

    const fetchAvailableLots = async (bId) => {
        setLotsLoading(true);
        try {
            const { data } = await client.get('/transactions/drying/available-lots', { params: { branch_id: bId } });
            setAvailableLots(data);
        } catch {
            message.error('Failed to load available lots');
        } finally {
            setLotsLoading(false);
        }
    };

    const addLot = (lot) => {
        if (selectedLots.find(l => l.palay_lot_id === lot.id)) {
            message.warning('Lot already added');
            return;
        }
        const wetWeight = Number(lot.current_quantity) || 0;
        // No silent default — an invented MC would drive a wrong yield figure.
        // Leave it blank and make the operator enter the meter reading.
        const initialMc = null;
        const expected = calcExpected(wetWeight, initialMc, targetMc);

        setSelectedLots(prev => [...prev, {
            palay_lot_id: lot.id,
            lot_number:   lot.lot_number,
            supplier:     lot.purchase?.partner?.name ?? '—',
            purchase_ticket: lot.purchase?.ticket_number ?? '—',
            location:     lot.location?.name ?? '—',
            wet_weight:   wetWeight,
            available_quantity: wetWeight,
            initial_mc:   initialMc,
            expected_dried_weight: expected,
        }]);
    };

    const removeLot = (palayLotId) => {
        setSelectedLots(prev => prev.filter(l => l.palay_lot_id !== palayLotId));
    };

    const updateLotField = (palayLotId, field, value) => {
        setSelectedLots(prev => prev.map(l => {
            if (l.palay_lot_id !== palayLotId) return l;
            const updated = { ...l, [field]: value };
            // Recalculate expected whenever wet_weight or initial_mc changes
            if (field === 'wet_weight' || field === 'initial_mc') {
                updated.expected_dried_weight = calcExpected(updated.wet_weight, updated.initial_mc, targetMc);
            }
            return updated;
        }));
    };

    const recalcAllExpected = (newTargetMc) => {
        setTargetMc(newTargetMc);
        setSelectedLots(prev => prev.map(l => ({
            ...l,
            expected_dried_weight: calcExpected(l.wet_weight, l.initial_mc, newTargetMc),
        })));
    };

    const totalWet      = selectedLots.reduce((s, l) => s + (l.wet_weight || 0), 0);
    const totalExpected = selectedLots.reduce((s, l) => s + (l.expected_dried_weight || 0), 0);
    const totalLoss     = totalWet - totalExpected;
    const lossPct       = totalWet > 0 ? (totalLoss / totalWet) * 100 : 0;
    const hasExpectedWeights = selectedLots.length > 0
        && targetMc != null
        && selectedLots.every(l => l.expected_dried_weight != null);

    const availableNotSelected = availableLots.filter(
        lot => !selectedLots.find(s => s.palay_lot_id === lot.id)
    );

    const onFinish = async (values) => {
        if (selectedLots.length === 0) {
            message.error('Add at least one palay lot');
            return;
        }

        const overweight = selectedLots.filter(l => l.wet_weight > l.available_quantity);
        if (overweight.length > 0) {
            message.error(
                `Wet weight exceeds available stock for: ${overweight.map(l => l.lot_number).join(', ')}`
            );
            return;
        }

        setSubmitting(true);
        try {
            await client.post('/transactions/drying', {
                branch_id:      values.branch_id,
                drying_method:  values.drying_method,
                facility_name:  values.facility_name,
                operator_name:  values.operator_name,
                scheduled_date: values.scheduled_date?.format('YYYY-MM-DD'),
                target_mc:      targetMc,
                remarks:        values.remarks,
                lots:           selectedLots.map(l => ({
                    palay_lot_id: l.palay_lot_id,
                    wet_weight:   l.wet_weight,
                    initial_mc:   l.initial_mc,
                })),
            });
            message.success('Drying order created');
            navigate('/drying');
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to create drying order');
        } finally {
            setSubmitting(false);
        }
    };

    const lotColumns = [
        { title: 'Lot #', dataIndex: 'lot_number', width: 120, render: (v) => <Text strong>{v}</Text> },
        { title: 'Supplier', dataIndex: 'supplier', ellipsis: true },
        { title: 'Purchase Ticket', dataIndex: 'purchase_ticket' },
        { title: 'Location', dataIndex: 'location' },
        {
            title: 'Wet Weight (kg)',
            dataIndex: 'wet_weight',
            width: 170,
            render: (v, r) => (
                <div>
                    <InputNumber
                        value={v}
                        min={0.001}
                        max={r.available_quantity}
                        step={0.001}
                        status={v > r.available_quantity ? 'error' : ''}
                        style={{ width: 130 }}
                        onChange={(val) => updateLotField(r.palay_lot_id, 'wet_weight', val)}
                    />
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Available: {fmt(r.available_quantity)} kg
                    </Text>
                </div>
            ),
        },
        {
            title: 'Initial MC (%) — Optional',
            dataIndex: 'initial_mc',
            width: 140,
            render: (v, r) => (
                <div>
                    <InputNumber
                        value={v}
                        min={1}
                        max={99}
                        step={0.1}
                        placeholder="Not measured"
                        style={{ width: 100 }}
                        addonAfter="%"
                        onChange={(val) => updateLotField(r.palay_lot_id, 'initial_mc', val)}
                    />
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>Leave blank without a meter</Text>
                </div>
            ),
        },
        {
            title: targetMc != null ? `Expected Dry @ ${targetMc}% MC` : 'Expected Dry Weight',
            dataIndex: 'expected_dried_weight',
            align: 'right',
            render: (v, r) => {
                if (v == null) {
                    return <Text type="secondary">—</Text>;
                }
                const loss = (r.wet_weight || 0) - (v || 0);
                const pct  = r.wet_weight > 0 ? (loss / r.wet_weight) * 100 : 0;
                return (
                    <div>
                        <Text strong style={{ color: '#059669' }}>{fmt(v)} kg</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>Loss: {fmt(pct)}%</Text>
                    </div>
                );
            },
        },
        {
            title: '',
            align: 'right',
            width: 50,
            render: (_, r) => (
                <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeLot(r.palay_lot_id)} />
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/drying')} style={{ borderRadius: 8 }} />
                <div>
                    <Title level={3} style={{ margin: 0, fontWeight: 800 }}>New Drying Order</Title>
                    <Text type="secondary">Select wet palay lots and record actual weights. Moisture readings are optional.</Text>
                </div>
            </div>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Card className="premium-card" style={{ marginBottom: 20 }}>
                    <Title level={5} style={{ marginTop: 0 }}>Drying Details</Title>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="branch_id" label="Branch" rules={[{ required: true }]}>
                                <Select placeholder="Select branch" onChange={(v) => { setBranchId(v); setSelectedLots([]); }} style={{ height: 40 }}>
                                    {branches.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="drying_method" label="Drying Method" rules={[{ required: true }]} initialValue="SUN">
                                <Select style={{ height: 40 }}>
                                    <Option value="SUN">☀️ Sun Drying</Option>
                                    <Option value="MECHANICAL">⚙️ Mechanical Dryer</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="facility_name" label="Facility / Floor Name">
                                <Input placeholder="e.g. Drying Floor A" style={{ height: 40 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="operator_name" label="Operator">
                                <Input placeholder="Staff name" style={{ height: 40 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                name="scheduled_date"
                                label="Scheduled Date"
                                initialValue={dayjs()}
                                rules={[{ required: true, message: 'Please select the scheduled date' }]}
                            >
                                <DatePicker style={{ width: '100%', height: 40 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label="Target MC (%) — Optional">
                                <InputNumber
                                    value={targetMc}
                                    min={10}
                                    max={25}
                                    step={0.5}
                                    addonAfter="%"
                                    placeholder="Not measured"
                                    style={{ width: '100%' }}
                                    onChange={recalcAllExpected}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={12}>
                            <Form.Item name="remarks" label="Remarks">
                                <Input placeholder="Optional notes..." style={{ height: 40 }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Lot Selection */}
                <Card className="premium-card" style={{ marginBottom: 20 }}>
                    <Alert
                        type="info"
                        showIcon
                        message="No moisture meter? Leave Initial MC and Target MC blank. Enter the measured wet kilograms now and the actual dried kilograms when completing the order; the system will calculate the weight loss."
                        style={{ marginBottom: 16 }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <Title level={5} style={{ margin: 0 }}>Select Palay Lots</Title>
                        {!branchId && <Text type="secondary">Select a branch first to see available lots.</Text>}
                    </div>

                    {branchId && (
                        lotsLoading ? <Spin /> : (
                            availableNotSelected.length === 0 ? (
                                <Alert type="info" message="No UNDRIED lots available for this branch." showIcon />
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {availableNotSelected.map(lot => (
                                        <Button
                                            key={lot.id}
                                            onClick={() => addLot(lot)}
                                            style={{ height: 'auto', padding: '8px 14px', borderRadius: 8, textAlign: 'left' }}
                                            icon={<PlusOutlined />}
                                        >
                                            <span>
                                                <Text strong>{lot.lot_number}</Text>
                                                <br />
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    {fmt(lot.current_quantity)} kg
                                                    {' · '}
                                                    {(lot.effective_mc ?? lot.moisture_content) != null
                                                        ? `MC: ${Number(lot.effective_mc ?? lot.moisture_content).toFixed(1)}%`
                                                        : 'MC: not set'}
                                                    {lot.purchase?.partner?.name ? ` · ${lot.purchase.partner.name}` : ''}
                                                </Text>
                                            </span>
                                        </Button>
                                    ))}
                                </div>
                            )
                        )
                    )}
                </Card>

                {/* Selected Lots Table */}
                {selectedLots.length > 0 && (
                    <Card className="premium-card" style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <Title level={5} style={{ margin: 0 }}>Selected Lots for Drying ({selectedLots.length})</Title>
                            {hasExpectedWeights && <Tag color="blue" icon={<CalculatorOutlined />}>
                                Formula: Dried = Wet × (100 − Initial MC) ÷ (100 − {targetMc})
                            </Tag>}
                        </div>

                        <Table
                            dataSource={selectedLots}
                            columns={lotColumns}
                            rowKey="palay_lot_id"
                            pagination={false}
                            size="middle"
                            scroll={{ x: 800 }}
                        />

                        <Divider />
                        <Row gutter={[24, 16]}>
                            <Col xs={12} sm={6}>
                                <Statistic title="Total Wet Weight" value={totalWet} suffix="kg" precision={2} />
                            </Col>
                            {hasExpectedWeights && (
                                <>
                                    <Col xs={12} sm={6}>
                                        <Statistic title={`Expected Dried (@ ${targetMc}% MC)`} value={totalExpected} suffix="kg" precision={2} valueStyle={{ color: '#059669' }} />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Statistic title="Expected Loss" value={totalLoss} suffix="kg" precision={2} valueStyle={{ color: '#dc2626' }} />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Statistic title="Loss %" value={lossPct} suffix="%" precision={2} valueStyle={{ color: lossPct > 15 ? '#dc2626' : lossPct > 10 ? '#d97706' : '#059669' }} />
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Card>
                )}

                <Space>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={submitting}
                        icon={<SaveOutlined />}
                        disabled={selectedLots.length === 0}
                        style={{ height: 44, borderRadius: 8, fontWeight: 600, paddingInline: 24 }}
                    >
                        Create Drying Order
                    </Button>
                    <Button onClick={() => navigate('/drying')} style={{ height: 44, borderRadius: 8 }}>Cancel</Button>
                </Space>
            </Form>
        </div>
    );
};

export default DryingForm;
